import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import {
  createOrder, initiatePayment, saveDraft, getShippingConfig,
} from '../utils/api';
import { BD_DIVISIONS, BD_DISTRICTS, PAYMENT_METHODS } from '../utils/helpers';
import toast from 'react-hot-toast';
import styles from './CheckoutPage.module.css';

const DEFAULT_SHIPPING = {
  inside_dhaka:            60,
  outside_dhaka:           120,
  free_shipping_threshold: 999,
  free_shipping_enabled:   true,
};

export default function CheckoutPage() {
  const { items, subtotal, clearCart } = useCart();
  const { user }   = useAuth();
  const navigate   = useNavigate();

  const [loading, setLoading]       = useState(false);
  const [coupon, setCoupon]         = useState('');
  const [txnId, setTxnId]           = useState('');
  const [paymentMethod, setPaymentMethod] = useState('cod');
  const [shippingConfig, setShippingConfig] = useState(DEFAULT_SHIPPING);
  const [draftId, setDraftId]       = useState(null);
  const draftTimer                  = useRef(null);

  const [form, setForm] = useState({
    name:       user?.name  || '',
    phone:      user?.phone || '',
    email:      user?.email || '',
    division:   '',
    district:   '',
    upazila:    '',
    address:    '',
    postalCode: '',
  });

  // Load dynamic shipping config from backend
  useEffect(() => {
    getShippingConfig()
      .then(r => setShippingConfig(r.data))
      .catch(() => {});
  }, []);

  // Calculate shipping dynamically
  const isDhaka    = form.division?.toLowerCase() === 'dhaka';
  const freeThreshold = shippingConfig.free_shipping_threshold;
  const shipping   = isDhaka
    ? (shippingConfig.free_shipping_enabled && subtotal >= freeThreshold ? 0 : shippingConfig.inside_dhaka)
    : shippingConfig.outside_dhaka;
  const total      = subtotal + shipping;
  const districts  = form.division ? BD_DISTRICTS[form.division] || [] : [];

  // Auto-save draft whenever form changes (debounced 3 seconds)
  useEffect(() => {
    if (!items?.length) return;
    if (!form.name && !form.phone && !form.division) return; // don't save empty forms

    if (draftTimer.current) clearTimeout(draftTimer.current);
    draftTimer.current = setTimeout(() => {
      saveDraft({
        draftId,
        items:           items.map(i => ({ product: i._id, quantity: i.quantity, variant: i.variant })),
        shippingAddress: form,
        paymentMethod,
        couponCode:      coupon,
      })
        .then(r => { if (r.data._id && !draftId) setDraftId(r.data._id); })
        .catch(() => {});
    }, 3000);

    return () => clearTimeout(draftTimer.current);
  }, [form, paymentMethod, items, coupon, draftId]);

  const handleChange = (e) => setForm(f => ({ ...f, [e.target.name]: e.target.value }));

  const handleDivisionChange = (e) => {
    setForm(f => ({ ...f, division: e.target.value, district: '' }));
  };

  const handlePaymentChange = (e) => {
    setPaymentMethod(e.target.value);
    setTxnId('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!form.division || !form.district || !form.address) {
      toast.error('Please fill in all required fields');
      return;
    }

    const manualMethods = ['bkash', 'nagad', 'rocket'];
    if (manualMethods.includes(paymentMethod) && !txnId.trim()) {
      toast.error(`Please enter your ${paymentMethod.charAt(0).toUpperCase() + paymentMethod.slice(1)} Transaction ID`);
      return;
    }

    setLoading(true);
    try {
      const orderData = {
        items: items.map(i => ({
          product:  i._id,
          quantity: i.quantity,
          variant:  i.variant,
        })),
        shippingAddress:      form,
        paymentMethod,
        couponCode:           coupon,
        note:                 txnId ? `${paymentMethod.toUpperCase()} TxnID: ${txnId}` : '',
        paymentTransactionId: txnId || null,
        draftId:              draftId || null,
      };

      const { data: order } = await createOrder(orderData);

      if (paymentMethod === 'sslcommerz') {
        const { data } = await initiatePayment(order._id);
        if (data.url) {
          window.location.href = data.url;
        } else {
          toast.error('Payment gateway error. Please try again.');
          setLoading(false);
        }
      } else {
        clearCart();
        navigate(`/order-confirm/${order._id}`);
      }

    } catch (err) {
      toast.error(err.response?.data?.message || 'Order failed. Please try again.');
      setLoading(false);
    }
  };

  const manualLabel = { bkash: 'bKash', nagad: 'Nagad', rocket: 'Rocket' };
  const isManual    = ['bkash', 'nagad', 'rocket'].includes(paymentMethod);

  return (
    <div className="container" style={{ padding: '1.5rem 1rem' }}>
      <h1 className={styles.title}>Checkout</h1>

      <form onSubmit={handleSubmit}>
        <div className={styles.layout}>

          {/* ── LEFT ── */}
          <div className={styles.left}>

            {/* Delivery Address */}
            <div className="card" style={{ padding: '1.5rem', marginBottom: '1rem' }}>
              <h3 className={styles.sectionTitle}>📍 Delivery Address</h3>
              <div className={styles.formGrid}>

                <div className="form-group">
                  <label className="form-label">Full Name *</label>
                  <input name="name" value={form.name} onChange={handleChange} className="form-input" placeholder="Rahim Uddin" required />
                </div>

                <div className="form-group">
                  <label className="form-label">Phone Number *</label>
                  <input name="phone" value={form.phone} onChange={handleChange} className="form-input" placeholder="01XXXXXXXXX" required />
                </div>

                <div className="form-group">
                  <label className="form-label">Email</label>
                  <input name="email" value={form.email} onChange={handleChange} className="form-input" type="email" placeholder="you@example.com" />
                </div>

                <div className="form-group">
                  <label className="form-label">Division *</label>
                  <select name="division" value={form.division} onChange={handleDivisionChange} className="form-input form-select" required>
                    <option value="">Select Division</option>
                    {BD_DIVISIONS.map(d => <option key={d} value={d}>{d}</option>)}
                  </select>
                </div>

                <div className="form-group">
                  <label className="form-label">District *</label>
                  <select name="district" value={form.district} onChange={handleChange} className="form-input form-select" required disabled={!form.division}>
                    <option value="">Select District</option>
                    {districts.map(d => <option key={d} value={d}>{d}</option>)}
                  </select>
                </div>

                <div className="form-group">
                  <label className="form-label">Upazila / Thana</label>
                  <input name="upazila" value={form.upazila} onChange={handleChange} className="form-input" placeholder="Upazila / Thana" />
                </div>

                <div className="form-group" style={{ gridColumn: '1 / -1' }}>
                  <label className="form-label">Full Address *</label>
                  <textarea name="address" value={form.address} onChange={handleChange} className="form-input" rows={2} placeholder="House no, Road, Area..." required />
                </div>

                <div className="form-group">
                  <label className="form-label">Postal Code</label>
                  <input name="postalCode" value={form.postalCode} onChange={handleChange} className="form-input" placeholder="1200" />
                </div>

              </div>
            </div>

            {/* Payment */}
            <div className="card" style={{ padding: '1.5rem' }}>
              <h3 className={styles.sectionTitle}>💳 Payment Method</h3>

              <div className={styles.paymentGrid}>
                {PAYMENT_METHODS.map(m => (
                  <label key={m.id} className={`${styles.payOption} ${paymentMethod === m.id ? styles.paySelected : ''}`}>
                    <input type="radio" name="payment" value={m.id} checked={paymentMethod === m.id} onChange={handlePaymentChange} />
                    <span className={styles.payIcon}>{m.icon}</span>
                    <span className={styles.payLabel}>{m.label}</span>
                    {m.id === 'cod' && <span className={styles.payBadge}>Popular</span>}
                  </label>
                ))}
              </div>

              {/* Manual payment instructions */}
              {isManual && (
                <div className={styles.payInstructions}>
                  <div className={styles.instrHeader}>
                    <span className={styles.instrIcon}>
                      {paymentMethod === 'bkash' ? '📱' : paymentMethod === 'nagad' ? '💰' : '🚀'}
                    </span>
                    <div>
                      <p className={styles.instrTitle}>Send money to {manualLabel[paymentMethod]}</p>
                      <p className={styles.instrNumber}><strong>01340319853</strong> ({manualLabel[paymentMethod]} Personal)</p>
                      {paymentMethod === 'rocket' ? (
                        <p className={styles.instrStep}>Dial <strong>*322#</strong> or open Rocket app → Send Money → Amount: <strong>৳{total.toLocaleString()}</strong></p>
                      ) : (
                        <p className={styles.instrStep}>Open {manualLabel[paymentMethod]} app → Send Money → Amount: <strong>৳{total.toLocaleString()}</strong></p>
                      )}
                    </div>
                  </div>
                  <div className={styles.txnInputWrap}>
                    <label className={styles.txnLabel}>
                      {manualLabel[paymentMethod]} Transaction ID <span style={{ color: 'var(--error)' }}>*</span>
                    </label>
                    <input
                      className="form-input"
                      placeholder="e.g. TXN1234567890"
                      value={txnId}
                      onChange={e => setTxnId(e.target.value.toUpperCase())}
                    />
                    <p className={styles.txnHint}>💡 Transaction ID appears in your app immediately after sending.</p>
                  </div>
                </div>
              )}

              {paymentMethod === 'sslcommerz' && (
                <div className={styles.methodInfo}>
                  <p>💳 You will be redirected to a secure payment page for card or internet banking.</p>
                </div>
              )}

              {paymentMethod === 'cod' && (
                <div className={styles.methodInfo}>
                  <p>📦 Pay cash when your order is delivered. Available across Bangladesh.</p>
                </div>
              )}
            </div>
          </div>

          {/* ── RIGHT ── */}
          <div className={styles.right}>
            <div className="card" style={{ padding: '1.25rem' }}>
              <h3 className={styles.sectionTitle}>🛒 Order Summary</h3>

              <div className={styles.orderItems}>
                {items.map(item => (
                  <div key={`${item._id}-${item.variant}`} className={styles.orderItem}>
                    <img src={item.thumbnail} alt={item.name} />
                    <div>
                      <p>{item.name}</p>
                      {item.variant && <p className={styles.variantText}>{item.variant}</p>}
                    </div>
                    <span>×{item.quantity}</span>
                    <strong>৳{((item.discountPrice || item.price) * item.quantity).toLocaleString()}</strong>
                  </div>
                ))}
              </div>

              <div className={styles.couponRow}>
                <input
                  className="form-input"
                  placeholder="Coupon code (optional)"
                  value={coupon}
                  onChange={e => setCoupon(e.target.value.toUpperCase())}
                />
                <button type="button" className="btn btn-outline btn-sm">Apply</button>
              </div>

              <div className={styles.summaryRows}>
                <div className={styles.summaryRow}>
                  <span>Subtotal</span>
                  <span>৳{subtotal.toLocaleString()}</span>
                </div>
                <div className={styles.summaryRow}>
                  <span>Shipping</span>
                  <span>
                    {shipping === 0
                      ? <span style={{ color: 'var(--success)', fontWeight: 700 }}>FREE</span>
                      : `৳${shipping}`
                    }
                  </span>
                </div>
                {isDhaka && shipping > 0 && (
                  <p style={{ fontSize: '.73rem', color: 'var(--text-muted)' }}>
                    Add ৳{(freeThreshold - subtotal).toLocaleString()} more for free delivery
                  </p>
                )}
                {shipping === 0 && (
                  <p style={{ fontSize: '.73rem', color: 'var(--success)' }}>🎉 Free delivery applied!</p>
                )}
                <div className={styles.sDivider} />
                <div className={`${styles.summaryRow} ${styles.totalRow}`}>
                  <span>Total</span>
                  <span>৳{total.toLocaleString()}</span>
                </div>
              </div>

              <button
                type="submit"
                className="btn btn-accent btn-lg btn-block"
                disabled={loading || items.length === 0}
                style={{ marginTop: '.625rem' }}
              >
                {loading ? (
                  <><div className="spinner spinner-sm" /> Processing…</>
                ) : paymentMethod === 'sslcommerz' ? (
                  `Proceed to Payment — ৳${total.toLocaleString()}`
                ) : (
                  `Place Order — ৳${total.toLocaleString()}`
                )}
              </button>

              <p className={styles.secureNote}>🔒 Secure & encrypted checkout</p>
            </div>
          </div>

        </div>
      </form>
    </div>
  );
}
